import React, { useEffect, useReducer, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import './App.css';
import './i18n';
import { FaLanguage } from 'react-icons/fa';
import { setLanguage } from './components/redux/Slices/busSlice';
import ProtectedRoute from './components/ProtectedRoute';

// Import all your components
import Login from './components/Auth/Login';
import LandingPage from './components/LandingPage/LandingPage';
import DashBoard from './components/Admin/DashBoard';
import BusFinderPanel from './components/Admin/BusFinderPanel/BusFinderPanel';
import ServicesDetails from './components/Admin/BusFinderPanel/ServicesDetails';
import RegisterBusService from './components/Admin/BusFinderPanel/RegisterBusService';
import OperatorsManagement from './components/Admin/BusFinderPanel/OperatorsManagement/OperatorsManagement';
import RegisterOperator from './components/Admin/BusFinderPanel/OperatorsManagement/RegisterOperator';
import OperatorDetails from './components/Admin/BusFinderPanel/OperatorsManagement/OperatorDetails';
import OperatorApprovalsManagement from './components/Admin/BusFinderPanel/OperatorsManagement/OperatorApprovalsManagement';
import BusStandsManagement from './components/Admin/BusFinderPanel/BusStandsManagement/BusStandsManagement';
import RegisterBusStand from './components/Admin/BusFinderPanel/BusStandsManagement/RegisterBusStand';
import RouteFinderPanel from './components/Admin/RouteFinderPanel/RouteFinderPanel';
import BusRoutesManage from './components/Admin/RouteFinderPanel/BusRouteManagement/BusRoutesManage';
import AddBusRoute from './components/Admin/RouteFinderPanel/BusRouteManagement/AddBusRoute';
import AddCityVillage from './components/Admin/RouteFinderPanel/GeoSpatialManagement/AddCityVillage';
import ManageCityVillage from './components/Admin/RouteFinderPanel/GeoSpatialManagement/ManageCityVillage';
import BusServiceApprovalPage from './components/Admin/BusFinderPanel/BusServiceApprovalPage';
import UserDashboard from './components/User/UserDashboard';
import UserBusFinder from './components/User/UserBusFinder';
import BusSeatSelection from './components/User/BusSeatSelection';
import MyBookings from './components/User/MyBookings';
import BusStandApprovals from './components/Admin/BusFinderPanel/BusStandsManagement/BusStandApprovals';
import VillageCityApprovals from './components/Admin/RouteFinderPanel/GeoSpatialManagement/VillageCityApprovals';
import RouteApprovals from './components/Admin/RouteFinderPanel/BusRouteManagement/RouteApprovals';
import EditBusService from './components/Admin/BusFinderPanel/EditBusService';
import EditOperator from './components/Admin/BusFinderPanel/OperatorsManagement/EditOperator';
import EditBusStand from './components/Admin/BusFinderPanel/BusStandsManagement/EditBusStand';
import EditCityVillage from './components/Admin/RouteFinderPanel/GeoSpatialManagement/EditCityVillage';
import EditRoute from './components/Admin/RouteFinderPanel/BusRouteManagement/EditRoute';
import AddTimetablePage from './components/Admin/BusFinderPanel/TimeTable/AddTimetablePage';
import ViewTimetablePage from './components/Admin/BusFinderPanel/TimeTable/ViewTimetablePage';
import BusDetails from './components/User/BusDetails';
import PrivacyPolicy from './components/footer/PrivacyPolicy';
import TermsOfService from './components/footer/TermsOfService';
import ContactUs from './components/footer/ContactUs';
import AboutTeam from './components/User/AboutTeam';
import UserGuide from './components/User/UserGuide';
import UserGuidemain from './components/Admin/UserGuidemain';
import ViewRouteDetails from './components/Admin/RouteFinderPanel/BusRouteManagement/ViewRouteDetails';

function App() {
  return (
    <Router>
      <div className="App">
        <MainContent />
      </div>
    </Router>
  );
}

function MainContent() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();

  // Force re-render when i18n language changes
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  // Track whether the user has manually changed the language on this user session.
  // This way, after they pick English, we don't override their choice.
  const userManuallyChoseRef = useRef(false);

  // Routes where language switcher should appear (only user-facing pages)
  const showLanguageSwitcherRoutes = [
    '/',
    '/seat-selection',
    '/mybookings'
  ];

  // Determine if current route is an admin/management route
  const isAdminRoute =
    location.pathname.startsWith('/management-panel') ||
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/busfinderpanel') ||
    location.pathname.startsWith('/bus-finder') ||
    location.pathname.startsWith('/route-finder-panel') ||
    location.pathname.startsWith('/route-finder') ||
    location.pathname === '/login';

  // Check if language switcher should be visible
  const shouldShowLanguageSwitcher = showLanguageSwitcherRoutes.includes(location.pathname);

  // Normalize current language (handles 'hi-IN' -> 'hi', 'en-US' -> 'en')
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  // Unified language change function
  const changeLanguage = (lng, isManual = false) => {
    if (isManual) {
      userManuallyChoseRef.current = true;
    }
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
    localStorage.setItem('preferred-language', lng);
    dispatch(setLanguage(lng));
  };

  // Re-render when i18n language changes
  useEffect(() => {
    const handleLanguageChanged = () => forceUpdate();
    i18n.on('languageChanged', handleLanguageChanged);
    return () => i18n.off('languageChanged', handleLanguageChanged);
  }, [i18n]);

  // Main effect: runs on route change
  useEffect(() => {
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

    if (isAdminRoute) {
      // Admin routes: force English
      if (currentLanguage !== 'en') {
        changeLanguage('en');
      }
      return;
    }

    if (shouldShowLanguageSwitcher) {
      // ✅ User just arrived on a user-facing page:
      //    - If user hasn't manually chosen anything this session, default to Hindi.
      //    - If user already manually chose (e.g. English), keep their choice.
      const savedLang = localStorage.getItem('preferred-language');

      if (!userManuallyChoseRef.current) {
        if (currentLanguage !== 'hi') {
          changeLanguage('hi');
        }
      } else if (savedLang && currentLanguage !== savedLang) {
        changeLanguage(savedLang);
      }
    }
  }, [location.pathname, isAdminRoute, shouldShowLanguageSwitcher, i18n]);

  return (
    <>
      {/* Language switcher – only on designated user pages */}
      {shouldShowLanguageSwitcher && (
        <div
          className="language-switcher"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            backgroundColor: 'white',
            padding: '10px',
            borderRadius: '5px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
          }}
        >
          <button
            onClick={() => changeLanguage('en', true)}
            style={{
              margin: '0',
              padding: '5px 10px',
              backgroundColor: currentLang === 'en' ? '#007bff' : '#f8f9fa',
              color: currentLang === 'en' ? 'white' : 'black',
              border: '1px solid #ddd',
              borderRadius: '3px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '14px'
            }}
          >
            <FaLanguage /> English
          </button>
          <button
            onClick={() => changeLanguage('hi', true)}
            style={{
              margin: '0',
              padding: '5px 10px',
              backgroundColor: currentLang === 'hi' ? '#007bff' : '#f8f9fa',
              color: currentLang === 'hi' ? 'white' : 'black',
              border: '1px solid #ddd',
              borderRadius: '3px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '14px'
            }}
          >
            <FaLanguage /> हिन्दी
          </button>
        </div>
      )}

      <Routes>
        <Route path="/" element={<UserDashboard />} />
        <Route path="/login" element={<Login />} />
        <Route path="/management-panel" element={<LandingPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashBoard />
            </ProtectedRoute>
          }
        />
        <Route path="/guide" element={<UserGuidemain />} />

        <Route
          path="/busfinderpanel"
          element={
            <ProtectedRoute>
              <BusFinderPanel />
            </ProtectedRoute>
          }
        />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/about-team" element={<AboutTeam />} />
        <Route
          path="/bus-finder/bus-services/manage"
          element={
            <ProtectedRoute>
              <ServicesDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/bus-services/register"
          element={
            <ProtectedRoute>
              <RegisterBusService />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/bus-services/edit/:busNo"
          element={
            <ProtectedRoute>
              <EditBusService />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/operators/manage"
          element={
            <ProtectedRoute>
              <OperatorsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/operators/register"
          element={
            <ProtectedRoute>
              <RegisterOperator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/operators/edit/:operatorNumber"
          element={
            <ProtectedRoute>
              <EditOperator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/operators/view/:operatorNumber"
          element={
            <ProtectedRoute>
              <OperatorDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/bus-services/approvals"
          element={
            <ProtectedRoute>
              <BusServiceApprovalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/operators/approvals"
          element={
            <ProtectedRoute>
              <OperatorApprovalsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/bus-stops/manage"
          element={
            <ProtectedRoute>
              <BusStandsManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/bus-stops/register"
          element={
            <ProtectedRoute>
              <RegisterBusStand />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/bus-stops/approvals"
          element={
            <ProtectedRoute>
              <BusStandApprovals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/bus-stands/edit/:busStandCode"
          element={
            <ProtectedRoute>
              <EditBusStand />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/timetables/add"
          element={
            <ProtectedRoute>
              <AddTimetablePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-finder/timetables/view"
          element={
            <ProtectedRoute>
              <ViewTimetablePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder-panel"
          element={
            <ProtectedRoute>
              <RouteFinderPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/routes/add"
          element={
            <ProtectedRoute>
              <AddBusRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/routes/manage"
          element={
            <ProtectedRoute>
              <BusRoutesManage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/routes/edit/:id"
          element={
            <ProtectedRoute>
              <EditRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/group-routes/create"
          element={
            <ProtectedRoute>
              <AddCityVillage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/group-routes/manage"
          element={
            <ProtectedRoute>
              <ManageCityVillage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/group-routes/approvals"
          element={
            <ProtectedRoute>
              <VillageCityApprovals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/city-village/edit/:id"
          element={
            <ProtectedRoute>
              <EditCityVillage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/routes/view/:routePathNo"
          element={
            <ProtectedRoute>
              <ViewRouteDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/route-finder/routes/approvals"
          element={
            <ProtectedRoute>
              <RouteApprovals />
            </ProtectedRoute>
          }
        />
        <Route path="/user-bus-finder" element={<UserBusFinder />} />
        <Route path="/user-guide" element={<UserGuide />} />
        <Route path="/bus-details" element={<BusDetails />} />
        <Route path="/seat-selection" element={<BusSeatSelection />} />
        <Route path="/mybookings" element={<MyBookings />} />
      </Routes>
    </>
  );
}

export default App;